import React, { useEffect, useState } from "react";
import { store } from "../../redux/oldStore.tsx";
import { toast } from "react-toastify";

const FAIR_TYPES = [
  "National",
  "Affiliate",
  "State",
  "Regional",
  "Local",
  "District",
  "County",
  "School",
];

const STATES = [
  ["AL", "Alabama"],
  ["AK", "Alaska"],
  ["AZ", "Arizona"],
  ["AR", "Arkansas"],
  ["CA", "California"],
  ["CO", "Colorado"],
  ["CT", "Connecticut"],
  ["DE", "Delaware"],
  ["DC", "District of Columbia"],
  ["FL", "Florida"],
  ["GA", "Georgia"],
  ["HI", "Hawaii"],
  ["ID", "Idaho"],
  ["IL", "Illinois"],
  ["IN", "Indiana"],
  ["IA", "Iowa"],
  ["KS", "Kansas"],
  ["KY", "Kentucky"],
  ["LA", "Louisiana"],
  ["ME", "Maine"],
  ["MD", "Maryland"],
  ["MA", "Massachusetts"],
  ["MI", "Michigan"],
  ["MN", "Minnesota"],
  ["MS", "Mississippi"],
  ["MO", "Missouri"],
  ["MT", "Montana"],
  ["NE", "Nebraska"],
  ["NV", "Nevada"],
  ["NH", "New Hampshire"],
  ["NJ", "New Jersey"],
  ["NM", "New Mexico"],
  ["NY", "New York"],
  ["NC", "North Carolina"],
  ["ND", "North Dakota"],
  ["OH", "Ohio"],
  ["OK", "Oklahoma"],
  ["OR", "Oregon"],
  ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"],
  ["SC", "South Carolina"],
  ["SD", "South Dakota"],
  ["TN", "Tennessee"],
  ["TX", "Texas"],
  ["UT", "Utah"],
  ["VT", "Vermont"],
  ["VA", "Virginia"],
  ["WA", "Washington"],
  ["WV", "West Virginia"],
  ["WI", "Wisconsin"],
  ["WY", "Wyoming"],
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function messageOf(r, fallback) {
  if (!r) return fallback;
  if (r.Message) return r.Message;
  if (typeof r.Data === "string" && r.Data) return r.Data;
  return fallback;
}

function prettyDate(iso) {
  if (!iso || iso.indexOf("-") < 0) return iso || "";
  const parts = iso.split("-");
  const month = MONTHS[Number(parts[1]) - 1];
  if (!month) return iso;
  return `${month} ${Number(parts[2])}, ${parts[0]}`;
}

const emptyBill = { paidOn: "", validThrough: "", amount: "", notes: "" };

/**
 * Admin editor for an NHD contest site. Posts JSON to Contests/SetupNewClient
 * instead of letting the browser submit the page.
 */
export default function SiteEditor({ clientId, onClose, onSaved }) {
  const [tab, setTab] = useState("info");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [client, setClient] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [fairs, setFairs] = useState([]);
  const [clients, setClients] = useState([]);
  const [bill, setBill] = useState(emptyBill);

  useEffect(() => {
    let cancelled = false;
    store.server
      .getApi(`Contests/ClientEditor?clientId=${clientId || 0}`)
      .then((r) => {
        if (cancelled) return;
        if (r && r.Success && r.Value && r.Value.client) {
          setClient(r.Value.client);
          setAffiliates(r.Value.affiliates || []);
          setFairs(r.Value.fairs || []);
          setClients(r.Value.clients || []);
          setReady(true);
        } else {
          toast.error(messageOf(r, "Could not open the site editor"));
          onClose();
        }
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Could not open the site editor");
        onClose();
      });
    return () => {
      cancelled = true;
    };
    // Reload only when the site id changes. onClose just dismisses the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const setField = (key, value) => {
    setClient((prev) => ({ ...prev, [key]: value }));
  };

  const saveInfo = (e) => {
    e.preventDefault();
    if (!client || busy) return;
    const mainTitle = (client.mainTitle || "").trim();
    const siteBinding = (client.siteBinding || "").trim();
    if (!mainTitle) {
      toast.error("Enter a name for the site.");
      return;
    }
    if (!siteBinding.toLowerCase().endsWith(".nhd.org")) {
      toast.error("Enter a site address that ends with .nhd.org, such as utah.nhd.org.");
      return;
    }

    setBusy(true);
    store.server
      .postApi("Contests/SetupNewClient", {
        clientId: client.clientId || 0,
        mainTitle,
        firstName: client.firstName || "",
        lastName: client.lastName || "",
        email: client.email || "",
        phone: client.phone || "",
        siteBinding,
        fairType: client.fairType || "Regional",
        state: client.state || "",
        affiliate: Number(client.affiliate) || 0,
      })
      .then((r) => {
        setBusy(false);
        if (r && r.Success) {
          toast.success(messageOf(r, "Site saved."));
          onSaved();
        } else {
          toast.error(messageOf(r, "Could not save the site."));
        }
      })
      .catch(() => {
        setBusy(false);
        toast.error("Could not save the site.");
      });
  };

  const savePromotion = (e) => {
    e.preventDefault();
    if (busy || fairs.length === 0) return;
    const promote = fairs
      .map((f) => `${f.id}|${f.canPromoteTo ? "true" : "false"}`)
      .join(":");
    const targets = fairs
      .map((f) => `${f.id}|${f.promoteToClientId ? f.promoteToClientId : "null"}`)
      .join(":");
    setBusy(true);
    store.server
      .getApi(`Contests/UpdatePromotion?data=${encodeURIComponent(`${promote}-${targets}`)}`)
      .then((r) => {
        setBusy(false);
        if (r && r.Success) toast.success(messageOf(r, "Promotion saved."));
        else toast.error(messageOf(r, "Could not save promotion."));
      })
      .catch(() => {
        setBusy(false);
        toast.error("Could not save promotion.");
      });
  };

  const savePayment = (e) => {
    e.preventDefault();
    if (!client || busy) return;
    if (!bill.paidOn || !bill.validThrough) {
      toast.error("Enter the date paid and the new valid-through date.");
      return;
    }
    setBusy(true);
    store.server
      .postApi("Contests/RecordPayment", {
        clientId: client.clientId,
        paidOn: bill.paidOn,
        validThrough: bill.validThrough,
        amount: Number(bill.amount) || 0,
        notes: bill.notes || "",
      })
      .then((r) => {
        setBusy(false);
        if (r && r.Success) {
          toast.success(messageOf(r, "Payment recorded."));
          setClient((prev) => ({
            ...prev,
            licenseDueText: prettyDate(bill.validThrough),
          }));
          setBill(emptyBill);
        } else {
          toast.error(messageOf(r, "Could not record the payment."));
        }
      })
      .catch(() => {
        setBusy(false);
        toast.error("Could not record the payment.");
      });
  };

  const removeSite = () => {
    if (!client || !client.clientId || busy) return;
    if (!window.confirm("Remove this site? It will drop off the contest list.")) return;
    setBusy(true);
    store.server
      .getApi(`Contests/RemoveClient?clientId=${client.clientId}`)
      .then((r) => {
        setBusy(false);
        if (r && r.Success) {
          toast.success(messageOf(r, "Site removed."));
          onSaved();
        } else {
          toast.error(messageOf(r, "Could not remove the site."));
        }
      })
      .catch(() => {
        setBusy(false);
        toast.error("Could not remove the site.");
      });
  };

  const creating = !(Number(clientId) > 0);
  const stateKnown = STATES.some((s) => s[0] === (client && client.state));

  return (
    <div className="nhd-modal-backdrop" onClick={onClose}>
      <div
        className="nhd-modal nhd-modal-wide site-editor-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="site-editor-title"
      >
        <div className="nhd-modal-header">
          <h3 id="site-editor-title">{creating ? "Add site" : "Edit site"}</h3>
          <button type="button" className="close" aria-label="Close" onClick={onClose}>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="nhd-modal-body">
          {!ready || !client ? (
            <p>Loading site…</p>
          ) : (
            <>
              {!creating && (
                <div className="site-editor-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "info"}
                    className={tab === "info" ? "active" : ""}
                    onClick={() => setTab("info")}
                  >
                    Info
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "promotion"}
                    className={tab === "promotion" ? "active" : ""}
                    onClick={() => setTab("promotion")}
                  >
                    Promotion
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "billing"}
                    className={tab === "billing" ? "active" : ""}
                    onClick={() => setTab("billing")}
                  >
                    Billing
                  </button>
                </div>
              )}

              {tab === "info" && (
                <form onSubmit={saveInfo}>
                  <div className="site-editor-grid">
                    <div className="form-input site-editor-span">
                      <label htmlFor="site-main-title">Site name</label>
                      <input
                        id="site-main-title"
                        value={client.mainTitle || ""}
                        onChange={(e) => setField("mainTitle", e.target.value)}
                        placeholder="Fair or website name"
                      />
                    </div>
                    <div className="form-input site-editor-span">
                      <label htmlFor="site-binding">Site address</label>
                      <input
                        id="site-binding"
                        value={client.siteBinding || ""}
                        onChange={(e) => setField("siteBinding", e.target.value)}
                        placeholder="utah.nhd.org"
                        autoComplete="off"
                      />
                      <span className="site-editor-help">Must end with .nhd.org</span>
                    </div>
                    <div className="form-input">
                      <label htmlFor="site-fair-type">Contest level</label>
                      <select
                        id="site-fair-type"
                        value={client.fairType || "Regional"}
                        onChange={(e) => setField("fairType", e.target.value)}
                      >
                        {FAIR_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-input">
                      <label htmlFor="site-state">State</label>
                      <select
                        id="site-state"
                        value={client.state || ""}
                        onChange={(e) => setField("state", e.target.value)}
                      >
                        <option value="">Select state</option>
                        {client.state && !stateKnown && (
                          <option value={client.state}>{client.state}</option>
                        )}
                        {STATES.map(([code, name]) => (
                          <option key={code} value={code}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-input site-editor-span">
                      <label htmlFor="site-affiliate">Affiliate</label>
                      <select
                        id="site-affiliate"
                        value={String(client.affiliate || 0)}
                        onChange={(e) => setField("affiliate", Number(e.target.value) || 0)}
                      >
                        <option value="0">Select affiliate</option>
                        {affiliates.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                            {a.locationCode ? ` (${a.locationCode})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-input">
                      <label htmlFor="site-first">First name</label>
                      <input
                        id="site-first"
                        value={client.firstName || ""}
                        onChange={(e) => setField("firstName", e.target.value)}
                        placeholder="Primary contact"
                      />
                    </div>
                    <div className="form-input">
                      <label htmlFor="site-last">Last name</label>
                      <input
                        id="site-last"
                        value={client.lastName || ""}
                        onChange={(e) => setField("lastName", e.target.value)}
                        placeholder="Primary contact"
                      />
                    </div>
                    <div className="form-input">
                      <label htmlFor="site-email">Email</label>
                      <input
                        id="site-email"
                        type="email"
                        value={client.email || ""}
                        onChange={(e) => setField("email", e.target.value)}
                      />
                    </div>
                    <div className="form-input">
                      <label htmlFor="site-phone">Phone</label>
                      <input
                        id="site-phone"
                        value={client.phone || ""}
                        onChange={(e) => setField("phone", e.target.value)}
                        placeholder="(000) 000-0000"
                      />
                    </div>
                  </div>
                  <div className="nhd-modal-actions">
                    <button type="submit" className="btn-primary" disabled={busy}>
                      {busy ? "Saving…" : "Save"}
                    </button>
                    {!!(client && client.clientId) && (
                      <button
                        type="button"
                        className="btn-danger"
                        disabled={busy}
                        onClick={removeSite}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </form>
              )}

              {tab === "promotion" && (
                <form onSubmit={savePromotion}>
                  {fairs.length === 0 ? (
                    <p>This site has no contests to configure for promotion.</p>
                  ) : (
                    <>
                      <p>Choose which contests can receive promoted projects, and where each contest promotes.</p>
                      {fairs.map((f) => (
                        <div key={f.id} className="site-editor-fair">
                          <div className="site-editor-fair-name">{f.name || `Contest ${f.id}`}</div>
                          <label className="site-editor-check">
                            <input
                              type="checkbox"
                              checked={!!f.canPromoteTo}
                              onChange={(e) =>
                                setFairs((prev) =>
                                  prev.map((row) =>
                                    row.id === f.id
                                      ? { ...row, canPromoteTo: e.target.checked }
                                      : row
                                  )
                                )
                              }
                            />
                            Can be promoted to
                          </label>
                          <label className="form-input">
                            <span>Promotes to</span>
                            <select
                              aria-label={`Promotes to for ${f.name || f.id}`}
                              value={f.promoteToClientId ? String(f.promoteToClientId) : ""}
                              onChange={(e) => {
                                const value = e.target.value ? Number(e.target.value) : null;
                                setFairs((prev) =>
                                  prev.map((row) =>
                                    row.id === f.id ? { ...row, promoteToClientId: value } : row
                                  )
                                );
                              }}
                            >
                              <option value="">None</option>
                              {f.promoteToClientId &&
                                !clients.some((c) => c.id === f.promoteToClientId) && (
                                  <option value={f.promoteToClientId}>
                                    Contest {f.promoteToClientId}
                                  </option>
                                )}
                              {clients.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name || `Contest ${c.id}`}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ))}
                      <div className="nhd-modal-actions">
                        <button type="submit" className="btn-primary" disabled={busy}>
                          {busy ? "Saving…" : "Save promotion"}
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}

              {tab === "billing" && (
                <form onSubmit={savePayment}>
                  <div className="site-editor-facts">
                    <div>
                      <span>License fee</span>
                      <strong>{client.renewalFeeText || "$0.00"}</strong>
                    </div>
                    <div>
                      <span>Valid through</span>
                      <strong>{client.licenseDueText || "Not set"}</strong>
                    </div>
                  </div>
                  <h4>Record a payment</h4>
                  <div className="site-editor-grid">
                    <div className="form-input">
                      <label htmlFor="site-paid-on">Paid on</label>
                      <input
                        id="site-paid-on"
                        type="date"
                        value={bill.paidOn}
                        onChange={(e) => setBill({ ...bill, paidOn: e.target.value })}
                      />
                    </div>
                    <div className="form-input">
                      <label htmlFor="site-valid-through">Valid through</label>
                      <input
                        id="site-valid-through"
                        type="date"
                        value={bill.validThrough}
                        onChange={(e) => setBill({ ...bill, validThrough: e.target.value })}
                      />
                    </div>
                    <div className="form-input">
                      <label htmlFor="site-amount">Amount paid (dollars)</label>
                      <input
                        id="site-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={bill.amount}
                        onChange={(e) => setBill({ ...bill, amount: e.target.value })}
                      />
                    </div>
                    <div className="form-input site-editor-span">
                      <label htmlFor="site-notes">Notes</label>
                      <textarea
                        id="site-notes"
                        rows={3}
                        value={bill.notes}
                        onChange={(e) => setBill({ ...bill, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="nhd-modal-actions">
                    <button type="submit" className="btn-primary" disabled={busy}>
                      {busy ? "Saving…" : "Record payment"}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
